import { useRef, useState } from "react";
import { createAuthenticatedPublicClient } from "@connect/data/public/client";
import { classifyKycError } from "./classify-kyc-error";
import { customerApiHeaders } from "./customer-api-headers";
import { toAlpha3 } from "./draft-to-patch-body";

export interface UseCountryBootstrapParams {
  customerId: string | null | undefined;
  apiBaseUrl: string;
  clientHeaders: Record<string, string>;
}

export type CountryBootstrapOutcome = "submitted" | "unavailable" | "failed" | "in_flight";

export interface UseCountryBootstrapResult {
  submitting: boolean;
  error: Error | null;
  submitCountry: (country: string) => Promise<CountryBootstrapOutcome>;
}

async function patchCountry(
  customerId: string,
  apiBaseUrl: string,
  clientHeaders: Record<string, string>,
  country: string,
) {
  const client = createAuthenticatedPublicClient({
    baseUrl: apiBaseUrl,
    headers: await customerApiHeaders(clientHeaders),
  });
  return client.PATCH("/platform/v1/customers/{id}/kyc", {
    params: { path: { id: customerId } },
    body: { residentialAddress: { country: toAlpha3(country) } },
  });
}

export function useCountryBootstrap({
  customerId,
  apiBaseUrl,
  clientHeaders,
}: UseCountryBootstrapParams): UseCountryBootstrapResult {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const inFlightRef = useRef(false);

  async function submitCountry(country: string): Promise<CountryBootstrapOutcome> {
    if (inFlightRef.current) {
      return "in_flight";
    }
    if (!customerId) {
      setError(new Error("kyc_bootstrap_failed"));
      return "failed";
    }

    inFlightRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: apiError } = await patchCountry(
        customerId,
        apiBaseUrl,
        clientHeaders,
        country,
      );

      const failure = apiError ? classifyKycError({ error: apiError }) : undefined;
      if (failure?.kind === "terminal" && failure.outcome === "unavailable") {
        return "unavailable";
      }

      if (apiError || !data?.data) {
        setError(new Error("kyc_bootstrap_failed"));
        return "failed";
      }

      return "submitted";
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
      return "failed";
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  }

  return { submitting, error, submitCountry };
}
